import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components/index";
import { withTranslation, WithTranslation } from "react-i18next";
import { Modal, ModalTitle, ModalContent, DetailRowListItem } from "@lib/components";
import { UI } from "../../../../../core/ui";
import { logger } from "@lib/common";
import { RestaurantItemModal } from "../../../common/item-modal";
import { OrderNumber } from "@lib/components";
import { DetailRowList } from "@lib/components";
import { OrderTimeline } from "@lib/components";
import { action, observable } from "mobx";
import { Select } from "@lib/components";
import { Untrusive } from "@lib/common";
import cloneDeep from "lodash/cloneDeep";
import { nanoid } from "nanoid";
import _get from "lodash/get";
import _findIndex from "lodash/findIndex";
import styled from "styled-components";
import { FaPrint, FaTrash } from "react-icons/fa";

/* ─── New Figma-design modal styled components ─────────────────────────────── */

const NewModalWrap = styled.div`
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const NewModalHeader = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
`;

const NewModalHeaderLeft = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

const NewModalName = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #0c202a;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const NewModalEmail = styled.div`
	font-size: 12px;
	color: #4b4f5e;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const NewModalBadge = styled.div`
	flex-shrink: 0;
	background: #fb2f2f;
	color: #fff;
	font-size: 12px;
	font-weight: 700;
	padding: 3px 10px;
	border-radius: 99px;
	white-space: nowrap;
`;

const NewModalDivider = styled.hr`
	border: none;
	border-top: 1px solid #e9eaeb;
	margin: 0;
`;

const NewModalStatusSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const NewModalStatusLabel = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #535862;
`;

const StatusButtonsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
`;

const StatusBtn = styled.button<{ $active?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px 12px;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.15s, border-color 0.15s;
	box-shadow: 0 1px 2px rgba(10,13,18,0.05);
	${({ $active }) =>
		$active
			? `background: #fff3f3; border: 1px solid #fda29b; color: #fb2f2f;`
			: `background: #f1f4f7; border: 1px solid #d5d7da; color: #414651;`}
	&:disabled {
		cursor: default;
	}
	&:not(:disabled):hover {
		${({ $active }) =>
			$active
				? `background: #ffe4e4;`
				: `background: #e8eaed;`}
	}
`;

const StatusProgressWrap = styled.div`
	display: flex;
	align-items: center;
	gap: 0;
	padding: 4px 0 0;
`;

const StatusProgressStep = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	flex: 0 0 auto;
`;

const StatusProgressCircle = styled.div<{ $active: boolean }>`
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: ${(p) => (p.$active ? "#fb2f2f" : "#e9eaeb")};
	color: ${(p) => (p.$active ? "#fff" : "#6b7280")};
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 700;
	transition: background 0.2s;
`;

const StatusProgressStepLabel = styled.div<{ $active: boolean }>`
	font-size: 10px;
	font-weight: 600;
	color: ${(p) => (p.$active ? "#fb2f2f" : "#9da4ae")};
	white-space: nowrap;
	transition: color 0.2s;
`;

const StatusProgressLine = styled.div<{ $active: boolean }>`
	flex: 1;
	height: 2px;
	background: ${(p) => (p.$active ? "#fb2f2f" : "#e9eaeb")};
	margin-bottom: 20px;
	transition: background 0.2s;
`;

const NewModalDetailsSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const NewModalSectionTitle = styled.div`
	font-size: 13px;
	font-weight: 700;
	color: #4b4f5e;
	margin-bottom: 6px;
`;

const NewModalRow = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	padding: 6px 0;
	border-bottom: 1px solid #f3f4f6;
	&:last-child { border-bottom: none; }
`;

const NewModalRowLabel = styled.div`
	font-size: 13px;
	font-weight: 500;
	color: #4b4f5e;
	flex-shrink: 0;
`;

const NewModalRowValue = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #4f575e;
	text-align: right;
	word-break: break-word;
`;

/** Footer actions (Print / Delete). Same look as the v1 "Actions" options, laid out as buttons. */
const NewModalActionsRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
`;

const NewModalActionBtn = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 12px;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	background: #fff;
	color: #414651;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	box-shadow: 0 1px 2px rgba(10,13,18,0.05);
	transition: background 0.15s, border-color 0.15s;
	&:hover { background: #f9fafb; border-color: #9da4ae; }
	&:disabled { cursor: default; opacity: 0.6; }
`;

/** Printer picker (stacked modal, shown only when the venue has more than one enabled printer). */
const PrinterPickList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const PrinterPickItem = styled.button`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	text-align: left;
	padding: 10px 12px;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	background: #fff;
	color: #414651;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: background 0.15s, border-color 0.15s;
	&:hover { background: #f9fafb; border-color: #9da4ae; }
`;

type BookingStatus = T.Schema.Booking.BookingStatuses;

const STATUS_BUTTONS: Array<{ value: BookingStatus; label: string }> = [
	{ value: "cancelled",   label: "Cancelled" },
	{ value: "unconfirmed", label: "Unconfirmed" },
	{ value: "confirmed",   label: "Confirmed" },
	{ value: "seated",      label: "Seated" },
	{ value: "complete",    label: "Complete" },
];


interface Props extends WithTranslation { }

@inject("store") @observer
export class RestaurantBookingModalClass extends MobxComponent<Props> {

	@observable tab: number = 0;
	/**
	 * v2 layout: printer picker modal open (only when more than one printer is enabled).
	 * NOTE: must only be read inside this component's own render() — `renderNewLayout`
	 * runs inside RestaurantItemModal (not an observer), so reads there are not tracked.
	 */
	@observable printMenuOpen: boolean = false;

	@action setTab = (tab: number) => this.tab = tab;
	@action setPrintMenuOpen = (open: boolean) => this.printMenuOpen = open;

	enabledPrinters() {
		const r = this.injected.store.restaurant!;
		return (r.settings.printers || []).filter((p) => !p.disabled);
	}

	/** Sends the booking docket to one printer (shared by the v1 Actions select and the v2 button). */
	printBooking = async (item: T.Schema.Booking.BookingSchema, printer_id: string) => {
		const { store } = this.injected;
		try {
			Untrusive.start();
			const r = store.restaurant!;
			const type = "booking";
			const restaurant_id = r._id;
			const type_id = item._id;
			const job_id = nanoid();
			await store.api.print_receipt({ type, restaurant_id, type_id, printer_id, job_id });
			UI.notification.success("Booking sent to printer");
		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("An error occurred, try again soon or contact us", { timeout: 6000 });
		}
		finally {
			Untrusive.stop();
		}
	}

	/** v2 Print button: one printer prints straight away, several open the picker. */
	handlePrintClick = (item: T.Schema.Booking.BookingSchema) => {
		const printers = this.enabledPrinters();
		if (printers.length === 0) return;
		if (printers.length === 1) {
			void this.printBooking(item, printers[0]._id);
			return;
		}
		this.setPrintMenuOpen(true);
	}

	query = async () => {
		try {
			const { store } = this.injected;
			const { query } = store.router.s;
			const item = store.booking;
			const queryButNoItem = query._id && !item;
			const queryItemMismatch = query._id && item && item._id !== query._id;
			if (queryButNoItem || queryItemMismatch) {

				// CHECK ORDERS COLLECTION FOR ORDER
				const booking = store.bookings.items.find((o) => o._id === query._id);
				if (booking) {
					store.setBooking(cloneDeep(booking));
					return;
				}

				// QUERY ORDER IF NOT IN COLLECTION
				const _id = query._id;
				const response = await store.api.booking_find({ _id });
				if (response.outcome) {
					store.router.push(`/restaurant/${store.restaurant!._id}/bookings`);
					store.setBooking(null);
					UI.notification.error(response.message);
				}
				else {
					store.setBooking(response.item);
				}

			}
		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("Error finding booking, please try again", { timeout: 5000 });
		}
	}

	close = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		this.setPrintMenuOpen(false);
		store.router.push(`/restaurant/${r._id}/bookings`);
		store.setBooking(null);
	}

	clearSelectInput = (id: string) => {
		const el = document.getElementById(id) as HTMLSelectElement | null;
		if (el) {
			el.value = "";
		}
	}

	handleStatusSelect = async (item: T.Schema.Booking.BookingSchema, status: T.Schema.Booking.BookingStatuses) => {
		if (!status) return;
		this.clearSelectInput("booking-status-select");
		await this.injected.store.service.booking.update_status(item._id, status);
	}

	handleActionSelect = async (item: T.Schema.Booking.BookingSchema, value: string) => {

		this.clearSelectInput("booking-action-select");

		if (!value) {
			return;
		}

		const { store } = this.injected;

		if (value.indexOf("print-") !== -1) {
			const printer_id = value.split("print-")[1];
			await this.printBooking(item, printer_id);
		}

		if (value === "delete") {
			try {
				const proceed = confirm("Once deleted, it cannot be recovered. Delete the booking?");
				if (!proceed) {
					return;
				}
				Untrusive.start();
				const { _id } = item;
				const restaurant_id = store.restaurant!._id;
				const response = await store.api.booking_delete({ _id, restaurant_id });
				if (response.outcome) {
					UI.notification.error(response.message, { timeout: 6000 });
				}
				else {
					this.close();
					store.removeBooking(_id);
					UI.notification.success("Booking deleted");
				}
			}
			catch (e) {
				logger.captureException(e);
				UI.notification.error("An error occurred, try again soon or contact us", { timeout: 6000 });
			}
			finally {
				Untrusive.stop();
				this.clearSelectInput("booking-action-select");
			}
		}

	}

	handleDelete = async (item: T.Schema.Booking.BookingSchema) => {
		const { store } = this.injected;
		try {
			const proceed = confirm("Once deleted, it cannot be recovered. Delete the booking?");
			if (!proceed) return;
			Untrusive.start();
			const { _id } = item;
			const restaurant_id = store.restaurant!._id;
			const response = await store.api.booking_delete({ _id, restaurant_id });
			if (response.outcome) {
				UI.notification.error(response.message, { timeout: 6000 });
			} else {
				this.close();
				store.removeBooking(_id);
				UI.notification.success("Booking deleted");
			}
		} catch (e) {
			logger.captureException(e);
			UI.notification.error("An error occurred, try again soon or contact us", { timeout: 6000 });
		} finally {
			Untrusive.stop();
		}
	}

	renderNewLayout = (item: T.Schema.Booking.BookingSchema) => {
		const { store, t } = this.injected;
		const { restrictions } = store;
		const canDelete = restrictions.restaurant.bookings_delete;
		const printers = this.enabledPrinters();

		const detailRows: Array<[string, string]> = [
			["Booking For", t("datetimeFromTimestamp", { value: item.config.timestamp })],
			["No. Of People", t("number", { value: item.config.number_of_people })],
			["Placed", t("datetimeFromTimestamp", { value: item.created })],
			["Name", item.customer.name],
			["E-Mail", item.customer.email],
			["Phone", item.customer.phone],
		];

		// append custom field answers
		const customFields: T.Schema.CustomFieldValue[] | undefined = _get(item, "custom_booking_field_list");
		const extraRows: Array<[string, string]> = customFields
			? customFields.map((f) => {
				const answer = f.type === "checkbox" ? (f.answer === "yes" ? "✅" : "❌") : f.answer;
				return [f.question.label, answer] as [string, string];
			})
			: [];

		// notes before custom fields
		if (item.notes) {
			const notesIdx = detailRows.findIndex(([l]) => l === "Name");
			detailRows.splice(notesIdx, 0, ["Notes", item.notes]);
		}

		const allRows = [...detailRows, ...extraRows];

		return (
			<NewModalWrap>
				{/* Header */}
				<NewModalHeader>
					<NewModalHeaderLeft>
						<NewModalName>{item.customer.name}</NewModalName>
						<NewModalEmail>{item.customer.email}</NewModalEmail>
					</NewModalHeaderLeft>
					<NewModalBadge>#{item.number}</NewModalBadge>
				</NewModalHeader>

				<NewModalDivider />

				{/* Change Status */}
				<NewModalStatusSection>
					<NewModalStatusLabel>Change Status</NewModalStatusLabel>
					<StatusButtonsGrid>
						{STATUS_BUTTONS.map((btn) => (
							<StatusBtn
								key={btn.value}
								type="button"
								$active={item.status === btn.value}
								disabled={item.status === btn.value}
								onClick={() => this.handleStatusSelect(item, btn.value)}
							>
								{btn.label}
							</StatusBtn>
						))}
					</StatusButtonsGrid>
					<NewModalStatusLabel style={{ marginTop: 12}}>Current Status</NewModalStatusLabel>
					{/* Progress bar: both steps active once confirmed/seated/complete */}
					{(() => {
						const confirmedOnwards = ["confirmed", "seated", "complete"].includes(item.status);
						const isUnconfirmedOrAbove = item.status !== "cancelled";
						const step1Active = isUnconfirmedOrAbove;
						const step2Active = confirmedOnwards;
						const lineActive = confirmedOnwards;
						return (
							<StatusProgressWrap>
								<StatusProgressStep>
									<StatusProgressCircle $active={step1Active}>1</StatusProgressCircle>
									<StatusProgressStepLabel $active={step1Active}>Unconfirmed</StatusProgressStepLabel>
								</StatusProgressStep>
								<StatusProgressLine $active={lineActive} />
								<StatusProgressStep>
									<StatusProgressCircle $active={step2Active}>2</StatusProgressCircle>
									<StatusProgressStepLabel $active={step2Active}>Confirmed</StatusProgressStepLabel>
								</StatusProgressStep>
							</StatusProgressWrap>
						);
					})()}
				</NewModalStatusSection>

				<NewModalDivider />

				{/* Booking details */}
				<NewModalDetailsSection>
					<NewModalSectionTitle>Booking Details</NewModalSectionTitle>
					{allRows.map(([label, value]) => (
						<NewModalRow key={label}>
							<NewModalRowLabel>{label}</NewModalRowLabel>
							<NewModalRowValue>{value}</NewModalRowValue>
						</NewModalRow>
					))}
				</NewModalDetailsSection>

				{/* Actions: print (when printers are set up) + delete (permission-gated) */}
				{(printers.length > 0 || canDelete) && (
					<NewModalActionsRow>
						{printers.length > 0 && (
							<NewModalActionBtn
								type="button"
								aria-haspopup={printers.length > 1 ? "dialog" : undefined}
								onClick={() => this.handlePrintClick(item)}
							>
								<FaPrint style={{ fontSize: 14, flexShrink: 0 }} />
								Print Booking
							</NewModalActionBtn>
						)}
						{canDelete && (
							<NewModalActionBtn type="button" onClick={() => this.handleDelete(item)}>
								<FaTrash style={{ fontSize: 14, flexShrink: 0 }} />
								Delete Booking
							</NewModalActionBtn>
						)}
					</NewModalActionsRow>
				)}
			</NewModalWrap>
		);
	}

	renderHeader = (item: T.Schema.Booking.BookingSchema) => {
		return (
			<ModalContent paddinglr={20} paddingtb={25} className="flex-l-r-center no-border">
				<OrderNumber>
					#{item.number}
				</OrderNumber>
			</ModalContent>
		);
	}

	renderStatusSelect = (item: T.Schema.Booking.BookingSchema) => {
		// Legacy (v1) modal: "Seated" is a Reservations v2 status (live table docket); prod offered
		// the four statuses below only and the API rejects `seated` for v1 venues.
		const options: Array<{ label: string; value: T.Schema.Booking.BookingStatuses; disabled: boolean }> = [
			{ label: "Cancelled", value: "cancelled", disabled: false },
			{ label: "Un-Confirmed", value: "unconfirmed", disabled: false },
			{ label: "Confirmed", value: "confirmed", disabled: false },
			{ label: "Complete", value: "complete", disabled: false },
		];

		for (const [i, option] of options.entries()) {
			if (option.value === item.status) {
				options[i].label = `${option.label} (Current)`;
				options[i].disabled = true;
			}
		}

		return (
			<Select
				id="booking-status-select"
				className="no-round"
				placeholder="Change Status"
				options={options}
				onChange={(e) => this.handleStatusSelect(item, e.target.value as T.Schema.Booking.BookingStatuses)}
			/>
		);

	}

	renderActionsSelect = (item: T.Schema.Booking.BookingSchema) => {

		const { restrictions } = this.injected.store;
		const r = this.injected.store.restaurant!;

		const options = [];

		for (const p of r.settings.printers) {
			if (!p.disabled) {
				options.push({
					label: `Print - ${p.name}`,
					value: `print-${p._id}`,
				});
			}
		}

		if (restrictions.restaurant.bookings_delete) {
			options.push({
				label: "Delete Booking",
				value: "delete",
			});
		}

		if (options.length === 0) {
			return null;
		}

		return (
			<Select
				id="booking-action-select"
				className="no-round no-border-top"
				placeholder="Actions"
				options={options}
				onChange={(e) => this.handleActionSelect(item, e.target.value)}
			/>
		);

	}

	render() {

		const { store, t } = this.injected;
		const { query } = store.router.s;
		const _id = query._id || null;
		const item = store.booking;
		const ri = store.restaurant?.reservations_integration;
		const masterOn = ri?.enabled === true;

		return (
			<div>
				<RestaurantItemModal<T.Schema.Booking.BookingSchema>
					active={!!_id}
					item={item}
					query={this.query}
					close={this.close}>

					{(b) => {
						/* ── New Figma design (reservations enabled) ── */
						if (masterOn) {
							return this.renderNewLayout(b);
						}

						/* ── Legacy design (reservations disabled) ── */
						let bookingDetailFields = [
							{
								l: "Booking For",
								v: t("datetimeFromTimestamp", { value: b.config.timestamp }),
							},
							{
								l: "No. Of People",
								v: t("number", { value: b.config.number_of_people }),
							},
							{
								l: "Placed",
								v: t("datetimeFromTimestamp", { value: b.created }),
							},
							{
								l: "Last Updated",
								h: !!b.updated,
								v: b.updated ? t("datetimeFromTimestamp", { value: b.updated }) : "",
							},
							{
								l: "Notes",
								noTranslate: true,
								v: b.notes,
							},
							{
								l: "Name",
								noTranslate: true,
								v: b.customer.name,
							},
							{
								l: "E-Mail",
								noTranslate: true,
								v: b.customer.email,
							},
							{
								l: "Phone",
								noTranslate: true,
								v: b.customer.phone,
							},
						] as DetailRowListItem[];

						let customBookingFields = _get(b, 'custom_booking_field_list');

						if (customBookingFields) {
							let customBookingFileds = customBookingFields.map((field: T.Schema.CustomFieldValue): DetailRowListItem => {
								let answer = field.answer;
								if (field.type === "checkbox") {
									answer = (answer === "yes") ? '✅' : '❌'
								}
								return {
									l: field.question.label,
									v: answer,
									noTranslate: true,
								}
							});

							const idx = _findIndex(bookingDetailFields, (item: DetailRowListItem) => item.l === "Notes");
							if (idx === -1) {
								bookingDetailFields = [...bookingDetailFields, ...customBookingFileds];
							} else {
								bookingDetailFields.splice(idx + 1, 0, ...customBookingFileds);
							}
						}

						return (
							<div>

								{this.renderHeader(b)}
								{this.renderStatusSelect(b)}
								{this.renderActionsSelect(b)}

								<OrderTimeline
									status={b.status}
									isDelivery={false}
									isBooking={true}
								/>

								<ModalContent paddinglr={20} paddingtb={25}>
									<DetailRowList
										items={bookingDetailFields}
									/>
								</ModalContent>

							</div>
						)
					}}
				</RestaurantItemModal>

				{/* v2 printer picker — rendered here (observer render) so printMenuOpen is tracked */}
				{masterOn && item && (
					<Modal
						width={360}
						level={2}
						alignTop
						active={this.printMenuOpen}
						close={() => this.setPrintMenuOpen(false)}>
						<ModalTitle className="round-top">
							<h4>Print Booking</h4>
							<p className="small text-muted m-t-1">Select a printer</p>
						</ModalTitle>
						<ModalContent paddinglr={20} paddingtb={16}>
							<PrinterPickList>
								{this.enabledPrinters().map((p) => (
									<PrinterPickItem
										key={p._id}
										type="button"
										onClick={() => {
											this.setPrintMenuOpen(false);
											void this.printBooking(item, p._id);
										}}>
										<FaPrint style={{ fontSize: 14, flexShrink: 0 }} />
										{p.name}
									</PrinterPickItem>
								))}
							</PrinterPickList>
						</ModalContent>
					</Modal>
				)}
			</div>
		);
	}

}

export const RestaurantBookingModal = withTranslation()(RestaurantBookingModalClass);
